'use client'

import { parseCookies } from 'nookies'
import { useCallback, useRef } from 'react'

import type { HttpMethod, StringObject } from '~/core/@types/global'

import configuration from '~/configuration'

import {
  SESSION_COOKIE_CHUB_SIGNED_TOKEN,
  SESSION_COOKIE_IP,
  SESSION_COOKIE_NAME,
  SESSION_COOKIE_NAME_CHUB,
  SESSION_COOKIE_SIGNED_TOKEN
} from '../constants/cookies'
import { destroySessionCookiesClient } from '../utilities/catch-api-error'

// eslint-disable-next-line import/no-restricted-paths
import { getSlugTenantFromCareerHubUrl, isCareerHubApp } from '~/lib/utilities/pathname'
const AUTHORIZATION_TOKEN_HEADER = 'Authorization'
const SIGNED_TOKEN_HEADER = 'X-Signed-Token'

const SKIP_TOKEN_CONSISTENCY_ENDPOINTS = new Set<string>([
  configuration.api.login,
  configuration.api.CHUBlogin,
  configuration.api.loginSSO,
  configuration.api.verifyReCaptcha
])

/**
 * @name useApiRequest
 * @description A hook to make API requests
 * 1. By default, it will use the `POST` method and will send the payload as
 * JSON
 * 2. Also, it will automatically add the [CSRF, Authorization] token to the request headers and
 * the App Check token if the SDK is initialized
 */
export function useApiRequest<Resp = unknown, Body = void>() {
  const headersRef = useRef<StringObject>({})

  return useCallback(
    async (params: {
      path: string
      body?: Body
      method?: HttpMethod
      extraParams?: { language?: string; timezone?: string }
      headers?: StringObject
    }) => {
      // Read cookies fresh on each request to handle tenant switching across tabs
      const cookies = parseCookies()
      const authorizationToken =
        typeof window !== 'undefined' && isCareerHubApp(window.location.pathname)
          ? cookies[SESSION_COOKIE_NAME_CHUB]
          : cookies[SESSION_COOKIE_NAME]
      const signedToken =
        typeof window !== 'undefined' && isCareerHubApp(window.location.pathname)
          ? cookies[SESSION_COOKIE_CHUB_SIGNED_TOKEN]
          : cookies[SESSION_COOKIE_SIGNED_TOKEN]
      const ip = cookies[SESSION_COOKIE_IP]
      const hasAnyAuthToken = !!authorizationToken || !!signedToken
      const shouldSkipTokenConsistencyCheck = SKIP_TOKEN_CONSISTENCY_ENDPOINTS.has(params.path)

      if (
        typeof window !== 'undefined' &&
        hasAnyAuthToken &&
        !shouldSkipTokenConsistencyCheck &&
        (!authorizationToken || !signedToken)
      ) {
        destroySessionCookiesClient(window.location.pathname)

        if (isCareerHubApp(window.location.pathname)) {
          const tenantSlug = getSlugTenantFromCareerHubUrl(window.location.pathname)
          if (tenantSlug) {
            window.location.href = configuration.path.careerHub.login(tenantSlug)
          } else {
            window.location.href = configuration.path.login
          }
        } else {
          window.location.href = configuration.path.login
        }
        return Promise.reject(new Error('Missing required auth tokens'))
      }

      const payload = JSON.stringify(params.body)

      // initialize the headers if they are not defined
      if (!headersRef.current) {
        headersRef.current = params.headers ?? {}
      }

      // add the Authorization token to the request headers if it exists
      if (authorizationToken) {
        headersRef.current[AUTHORIZATION_TOKEN_HEADER] = `Bearer ${authorizationToken}`
      }

      if (ip) {
        headersRef.current['IP'] = ip
      }

      if (signedToken) {
        headersRef.current[SIGNED_TOKEN_HEADER] = signedToken
      }

      if (params.headers?.[AUTHORIZATION_TOKEN_HEADER]) {
        headersRef.current[AUTHORIZATION_TOKEN_HEADER] = params.headers[AUTHORIZATION_TOKEN_HEADER]
      }

      // execute the fetch request
      return executeFetchRequest<Resp>(
        params.path,
        payload,
        params.method,
        params.extraParams,
        headersRef.current
      )
    },
    []
  )
}

async function executeFetchRequest<Resp = unknown>(
  url: string,
  payload: string,
  method = 'GET',
  extraParams?: { language?: string; timezone?: string },
  headers?: StringObject
) {
  const options: RequestInit = {
    method,
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      ...(headers ?? {})
    }
  }

  const methodsSupportingBody: HttpMethod[] = ['POST', 'PUT', 'PATCH']
  const supportsBody = methodsSupportingBody.includes(method as HttpMethod)

  if (payload && supportsBody) {
    const format = {
      payload: {
        ...JSON.parse(payload),
        ...extraParams
      }
    }
    options.body = JSON.stringify(format)
  }

  try {
    const response = await fetch(url, options)

    if (response.redirected) {
      return window.location.assign(response.url)
    }

    if (response.ok) {
      if (
        [configuration.api.downloadFile, configuration.api.chubDownloadFile].includes(response.url)
      ) {
        return (await new Response(response.body).blob()) as any
      }
      return (await response.json()) as Promise<Resp>
    }

    return Promise.reject(await response.json())
  } catch (error) {
    return Promise.reject(error)
  }
}
